import * as React from 'react';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { getYear } from 'date-fns';
import styled from 'styled-components';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';
import {
  ThemeProvider,
  BottomFixedContainer,
  BreakpointMountProvider,
  BreakpointMount,
  OffCanvasProvider,
  OffCanvasWrapper,
  BreakpointNames,
  BackToTopButton,
} from '@packages/ui';
import { useTranslations } from '@packages/i18n';
import { useUserInteraction } from '@lib/hooks';
import { DYModulesExport } from '@packages/common';
import { gtmEventDispatcher, WishlistHelperProvider } from '@packages/shared';
import { ScomRenewer } from '../ScomRenewer';
import { OriginalUrlCookieRenewer } from '../OriginalUrlCookieRenewer';
import { ClientSideLogger } from '../ClientSideLogger/ClientSideLogger';
import { DYCookieRenewer } from '../DYCookieRenewer/DYCookieRenewer';
import { Header } from '../Header/Header';

import footerLinks from '../../layoutConfigs/footer.json';
import { LoadingBarContainer } from './LoadingBarContainer';
import { MobileNavigation } from '../MobileNavigation';
import { Footer } from '../Footer/Footer';
import { GlobalClientRouter } from '../../containers/GlobalClientRouter/GlobalClientRouter';

const USERCENTRICS_ID = getPublicRuntimeConfig('USERCENTRICS_ID');
const LOCALE = getPublicRuntimeConfig('LOCALE');
const GTM_ID = getPublicRuntimeConfig('GTM_ID');
const DATALAYER_NAME = getPublicRuntimeConfig('DATALAYER_NAME');
const IS_E2E_TEST = getPublicRuntimeConfig('IS_E2E_TEST');

const UsercentricsProvider = dynamic(
  () => import('@moebel.de/usercentrics-client').then((mod) => mod.UsercentricsProvider),
  { ssr: true },
);

const Usercentrics = dynamic(() => import('@moebel.de/usercentrics-client').then((mod) => mod.Usercentrics), {
  ssr: true,
});

const BottomFixedWrapper = styled(BottomFixedContainer)`
  pointer-events: none;
  display: flex;
  flex-direction: column;
  & > * {
    pointer-events: all;
  }
`;

const Layout: React.FC<{ enableUC: boolean; enableGTM: boolean }> = ({ children, enableUC, enableGTM }) => {
  const [footerTitle, copyrightText] = useTranslations([
    'footerTitle',
    { key: 'copyrightText', values: { year: getYear(new Date()) } },
  ]);

  return (
    <WishlistHelperProvider>
      {enableGTM && (
        <Script>
          {`
              (function(w,d,s,l,i){w[l]=w[l]||[];var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','${DATALAYER_NAME}','${GTM_ID}');
          `}
        </Script>
      )}

      <ThemeProvider>
        <BreakpointMountProvider>
          <OffCanvasProvider>
            <LoadingBarContainer />
            <Header />
            <DYCookieRenewer />
            <ScomRenewer />
            <ClientSideLogger />
            <GlobalClientRouter />
            <OriginalUrlCookieRenewer />
            <DYModulesExport />
            {children}
            <BottomFixedWrapper>
              <BackToTopButton />
            </BottomFixedWrapper>
            <Footer links={footerLinks} title={footerTitle} copyrightText={copyrightText}></Footer>
            {enableUC && (
              <Usercentrics
                appRootElementId="__next"
                afterFirstLayerAcceptedAll={() => gtmEventDispatcher.ucAcceptedAllOnFirstLayer()}
                afterFirstLayerCloseAndAcceptedAll={() => gtmEventDispatcher.ucCloseAndAcceptedAllOnFirstLayer()}
                afterSecondLayerAcceptedAll={() => gtmEventDispatcher.ucAcceptedAllOnSecondlayer()}
                afterSecondLayerCloseAndAcceptedAll={() => gtmEventDispatcher.ucCloseAndAcceptedAllOnSecondLayer()}
                afterSettingsOpened={() => gtmEventDispatcher.ucSettingsOpened()}
                afterSettingsSaved={() => gtmEventDispatcher.ucSettingsSaved()}
              />
            )}
            <OffCanvasWrapper position="left">
              <BreakpointMount max={BreakpointNames.lg}>
                <MobileNavigation />
              </BreakpointMount>
            </OffCanvasWrapper>
          </OffCanvasProvider>
        </BreakpointMountProvider>
      </ThemeProvider>
    </WishlistHelperProvider>
  );
};

export const MainLayout: React.FC = ({ children }) => {
  const { query } = useRouter();
  const defer3rdParty = query?.['defer-3rd-party'];
  const isUserActive = useUserInteraction({ timeoutSeconds: 10, disabled: !defer3rdParty });
  const enableUC = isUserActive || !defer3rdParty || !defer3rdParty.includes('uc');
  const enableGTM = isUserActive || !defer3rdParty || !defer3rdParty.includes('gtm');

  if (enableUC && !IS_E2E_TEST) {
    return (
      <UsercentricsProvider
        disabled={!enableUC}
        settingsId={USERCENTRICS_ID}
        options={{
          language: LOCALE,
          appearance: 'wall',
          showCloseAndAcceptAllButton: true,
        }}
      >
        <Layout enableGTM={enableGTM} enableUC={enableUC}>
          {children}
        </Layout>
      </UsercentricsProvider>
    );
  }

  return (
    <Layout enableGTM={enableGTM} enableUC={false}>
      {children}
    </Layout>
  );
};
